import type { PropsWithChildren } from 'react';

import * as Sentry from '@sentry/react';
import type { History } from 'history';

import Button from 'src/app/components/Elements/Button';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';

interface ErrorModalProps {
  error: Error;
  componentStack: string | null;
  eventId: string | null;
  resetError(): void;
  errorSourceComponentName: string;
  errorLocation: string;
}

const ErrorModal = ({ error, componentStack, errorSourceComponentName, errorLocation }: ErrorModalProps) => {
  useSegmentEvent('error', {
    shouldTrackOnMount: true,
    payload: {
      errorMessage: 'Something went wrong',
      errorSummary: `Unknown error within ${errorSourceComponentName}`,
      api: '',
      errorCode: '',
      source: errorLocation,
    },
  });

  return (
    <div color="danger" className="m-2">
      <h4>Something went wrong.</h4>
      <div>Don&#8217;t worry, this error has been automatically logged to the Capsule team.</div>

      <div className="my-3">
        <Button style="secondary" size="small" onClick={() => window.location.reload()}>
          Reload Page
        </Button>
        &nbsp;
      </div>

      <details style={{ whiteSpace: 'pre-wrap' }}>
        <p className="text-danger mb-0">{error && error.toString()}</p>
        <pre>
          <code>{componentStack}</code>
        </pre>
      </details>
    </div>
  );
};

export interface ErrorBoundaryProps extends PropsWithChildren<{ errorSourceComponentName: string; history: History }> {}

export const ErrorBoundary = ({ children, errorSourceComponentName, history }: ErrorBoundaryProps) => {
  const pathname = history?.location?.pathname;
  const search = history?.location?.search;

  const errorLocation = pathname && search ? `${pathname}?${search}` : pathname ?? 'Source information missing.';

  return (
    <Sentry.ErrorBoundary
      fallback={props => (
        <ErrorModal {...props} errorSourceComponentName={errorSourceComponentName} errorLocation={errorLocation} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
